import { lazy, Suspense } from 'react';
import { RoleGuard, PermissionGuard } from '@emfood/shared';
import { Outlet, Navigate, createRoute, SearchSchemaInput } from '@tanstack/react-router';

import DashboardLayout from 'src/layouts/dashboard';
import { ROLES, PERMISSIONS } from 'src/api/roles-config';

import AppAuthGuard from 'src/components/auth/app-auth-guard';
import { SplashScreen, LoadingScreen } from 'src/components/loading-screen';

import { BuyerOrdersListSearchParamsSchema } from 'src/sections/orders/buyer-orders/search-params';

import { rootRoute } from './root';

const BuyerOrderDetails = lazy(() => import('src/sections/orders/buyer-order-details/view'));
const BuyerOrders = lazy(() => import('src/sections/orders/buyer-orders/view'));

function BuyerOrderDetailsWrapper() {
  const params = buyerOrderDetailsRoute.useParams();
  const { distributorId, distributorSiteId } = params;

  const hasOptionalParams = distributorId !== undefined || distributorSiteId !== undefined;

  if (
    (distributorId !== undefined && distributorSiteId === undefined) ||
    (distributorId === undefined && distributorSiteId !== undefined)
  ) {
    return <Navigate to="/403" replace />;
  }

  if (hasOptionalParams) {
    return (
      <RoleGuard requiredRoles={[ROLES.ADMIN, ROLES.ENTEGRA_USER]}>
        <BuyerOrderDetails />
      </RoleGuard>
    );
  }

  return (
    <RoleGuard requiredRoles={[ROLES.DISTRIBUTOR_ADMIN, ROLES.BUYER_ADMIN]}>
      <BuyerOrderDetails />
    </RoleGuard>
  );
}

const buyerOrdersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'buyer-orders',
  validateSearch: (search: unknown & SearchSchemaInput) =>
    BuyerOrdersListSearchParamsSchema.validateSync(search),
  component: () => (
    <AppAuthGuard splashScreen={<SplashScreen />}>
      <DashboardLayout>
        <Suspense fallback={<LoadingScreen />}>
          <Outlet />
        </Suspense>
      </DashboardLayout>
    </AppAuthGuard>
  ),
});

const buyerOrdersIndexRoute = createRoute({
  getParentRoute: () => buyerOrdersRoute,
  path: '/',
  component: () => (
    <PermissionGuard
      requiredPermissions={[
        PERMISSIONS.ORDERS_READ,
        PERMISSIONS.ORDERS_READ_ALL,
        PERMISSIONS.DISTRIBUTORS_READ_BUYER_ORDERS,
      ]}
    >
      <BuyerOrders />
    </PermissionGuard>
  ),
});

const buyerOrderDetailsRoute = createRoute({
  getParentRoute: () => buyerOrdersRoute,
  path: '$id/{-$distributorId}/{-$distributorSiteId}',
  component: () => <BuyerOrderDetailsWrapper />,
});

export const buyerOrdersRouteTree = buyerOrdersRoute.addChildren([
  buyerOrdersIndexRoute,
  buyerOrderDetailsRoute,
]);
